/**
 * Chart Modal Content Component
 *
 * Contains the chart and statistics panel
 */

import { format } from 'date-fns';
import type { ChartStatistics } from '../../types/chart.types';

interface ChartModalContentProps {
  data: any[];
  loading: boolean;
  statistics?: ChartStatistics;
  children: React.ReactNode; // Chart component
}

export function ChartModalContent({
  loading,
  statistics,
  children
}: ChartModalContentProps) {
  return (
    <div className="p-6 space-y-6">
      {/* Chart */}
      <div className="min-h-[400px]">
        {children}
      </div>

      {/* Statistics Panel */}
      {!loading && statistics && (
        <div
          className="rounded-xl border border-slate-700/50 p-4"
          style={{
            background:
              'linear-gradient(135deg, rgba(30, 41, 59, 0.5) 0%, rgba(51, 65, 85, 0.3) 100%)',
          }}
        >
          <h3 className="text-sm font-semibold text-slate-300 mb-3">
            📊 Statistics
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-xs text-slate-500 mb-1">Minimum</div>
              <div className="text-lg font-bold text-red-400">
                {statistics.min.value.toFixed(1)}
              </div>
              <div className="text-xs text-slate-600">
                {format(statistics.min.timestamp, 'MMM d, h:mm a')}
              </div>
            </div>

            <div>
              <div className="text-xs text-slate-500 mb-1">Maximum</div>
              <div className="text-lg font-bold text-green-400">
                {statistics.max.value.toFixed(1)}
              </div>
              <div className="text-xs text-slate-600">
                {format(statistics.max.timestamp, 'MMM d, h:mm a')}
              </div>
            </div>

            <div>
              <div className="text-xs text-slate-500 mb-1">Average</div>
              <div className="text-lg font-bold text-blue-400">
                {statistics.avg.toFixed(1)}
              </div>
            </div>

            {statistics.trend && (
              <div>
                <div className="text-xs text-slate-500 mb-1">Trend</div>
                <div
                  className={`text-lg font-bold ${
                    statistics.trend.direction === 'up'
                      ? 'text-green-400'
                      : statistics.trend.direction === 'down'
                      ? 'text-red-400'
                      : 'text-slate-400'
                  }`}
                >
                  {statistics.trend.direction === 'up'
                    ? '↑'
                    : statistics.trend.direction === 'down'
                    ? '↓'
                    : '→'}{' '}
                  {Math.abs(statistics.trend.value).toFixed(1)}%
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
